<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import EmpresasAPI from 'dashboard/api/empresas';

const props = defineProps({
  empresaId: {
    type: [String, Number],
    required: true,
  },
});

const router = useRouter();
const { t } = useI18n();

const conversations = ref([]);
const isLoading = ref(false);

const STATUS_DOT = {
  open: 'bg-n-teal-9',
  pending: 'bg-n-amber-9',
  snoozed: 'bg-n-blue-9',
  resolved: 'bg-n-slate-8',
};

const loadConversations = async () => {
  isLoading.value = true;
  try {
    const { data } = await EmpresasAPI.conversations(props.empresaId);
    conversations.value = data || [];
  } catch (error) {
    conversations.value = [];
  } finally {
    isLoading.value = false;
  }
};

const openConversation = conversation =>
  router.push({
    name: 'inbox_conversation',
    params: { conversationId: conversation.id },
  });

watch(() => props.empresaId, loadConversations);
onMounted(loadConversations);
</script>

<template>
  <div class="flex flex-col">
    <p
      v-if="!conversations.length && !isLoading"
      class="py-1 text-sm text-n-slate-10"
    >
      {{ t('EMPRESAS.HISTORIAL.EMPTY') }}
    </p>
    <ul v-else class="flex flex-col divide-y divide-n-weak/60">
      <li
        v-for="conversation in conversations"
        :key="conversation.uuid || conversation.id"
        class="flex items-center gap-2 py-2 cursor-pointer group"
        @click="openConversation(conversation)"
      >
        <span
          class="rounded-full size-2 shrink-0"
          :class="STATUS_DOT[conversation.status] || 'bg-n-slate-8'"
        />
        <div class="flex flex-col min-w-0">
          <span
            class="text-sm truncate text-n-slate-12 group-hover:text-n-blue-text"
          >
            {{
              conversation.contact?.name || t('EMPRESAS.HISTORIAL.NO_CONTACT')
            }}
          </span>
          <span class="text-xs truncate text-n-slate-10">
            #{{ conversation.id }}
            <template v-if="conversation.inbox?.name">
              · {{ conversation.inbox.name }}
            </template>
          </span>
        </div>
        <span
          v-if="conversation.last_activity_at"
          class="ml-auto text-xs shrink-0 text-n-slate-10"
        >
          {{ dynamicTime(conversation.last_activity_at) }}
        </span>
      </li>
    </ul>
  </div>
</template>
